/**
 * Crosshair customization store — Sprint 6.
 *
 * Persists to localStorage under `hupper.crosshair` — or
 * `hupper.crosshair.practice` on the free practice page, so a practice
 * visitor's crosshair tweaks never touch the real (embedded) game's
 * crosshair and vice versa. The Crosshair UI reads from here every frame;
 * the editor (CrosshairSettings) writes back on every change.
 */
import { isPracticeMode } from "../practiceMode";

export type CrosshairStyle = "cross" | "t-shape" | "dot" | "cross-dot";

export interface CrosshairConfig {
  /** Hex color (e.g. "#00ff88"). */
  color: string;
  /** Line thickness in px. */
  thickness: number;
  /** Static gap added on top of the dynamic spread (px). */
  gap: number;
  /** Length of each line segment (px). */
  length: number;
  /** Outline on/off + color + thickness. */
  outline: boolean;
  outlineColor: string;
  outlineThickness: number;
  /** Crosshair style. */
  style: CrosshairStyle;
  /** When true, the gap also grows with the live inaccuracy cone. */
  dynamic: boolean;
}

export const DEFAULT_CROSSHAIR: CrosshairConfig = {
  color: "#00ff88",
  thickness: 1,
  gap: 3,
  length: 6,
  outline: true,
  outlineColor: "#000000",
  outlineThickness: 1,
  style: "cross",
  dynamic: true,
};

const STORAGE_KEY = isPracticeMode ? "hupper.crosshair.practice" : "hupper.crosshair";

export function loadCrosshairConfig(): CrosshairConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_CROSSHAIR };
    const parsed = JSON.parse(raw) as Partial<CrosshairConfig>;
    return { ...DEFAULT_CROSSHAIR, ...parsed };
  } catch {
    return { ...DEFAULT_CROSSHAIR };
  }
}

export function saveCrosshairConfig(cfg: CrosshairConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
  } catch {
    /* localStorage unavailable — ignore */
  }
}

/** Subscribe to cross-tab + same-tab changes. */
const subscribers = new Set<(cfg: CrosshairConfig) => void>();
export function subscribeCrosshair(cb: (cfg: CrosshairConfig) => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}
export function notifyCrosshairChange(cfg: CrosshairConfig): void {
  saveCrosshairConfig(cfg);
  for (const cb of subscribers) cb(cfg);
}
